import { useMemo, useState } from "react";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Clock3,
  FileText,
  Gavel,
  Image as ImageIcon,
  Mail,
  MessageSquare,
  ShieldAlert,
  Trophy,
  UserRound,
  X,
  ZoomIn,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useMutation } from "@tanstack/react-query";

import { makeRequest } from "@/utils/makeRequest";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { queryClient } from "@/services/queryClient";

import { useDispute } from "@/hooks";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { Separator } from "@/components/ui/separator";

import {
  DisputeStatus,
  DisputeWinner,
  type Dispute,
} from "@/interfaces/dispute";

import { AdminVerificationDialog } from "@/components/admin-components/AdminVerificationDialog";

import type { Trade } from "@/interfaces/trade";
import type { TradeMessage } from "@/interfaces/message/message";
import TradeCommunicationDialog from "@/components/shared/dispute/TradeCommunicationDialog";

type PreviewFile = {
  url: string;
  type: "image" | "pdf" | "document";
};

const formatDate = (value?: string | null) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const getDisputeStatus = (status: string) => {
  switch (status) {
    case DisputeStatus.OPEN:
      return {
        label: "Open",
        className: "border-amber-500/30 bg-amber-500/10 text-amber-600",
      };

    case DisputeStatus.UNDER_REVIEW:
      return {
        label: "Under Review",
        className: "border-blue-500/30 bg-blue-500/10 text-blue-600",
      };

    case DisputeStatus.RESOLVED:
      return {
        label: "Resolved",
        className: "border-green-500/30 bg-green-500/10 text-green-600",
      };

    case DisputeStatus.REJECTED:
      return {
        label: "Rejected",
        className: "border-red-500/30 bg-red-500/10 text-red-600",
      };

    default:
      return {
        label: "Unknown",
        className: "border-gray-500/30 bg-gray-500/10 text-gray-600",
      };
  }
};

const getFullName = (
  user?: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
  } | null,
) => {
  if (!user) {
    return "Unknown user";
  }

  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();

  return name || user.email || "Unknown user";
};

const getInitials = (
  user?: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
  } | null,
) => {
  if (!user) {
    return "U";
  }

  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();

  if (name) {
    return name
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join("");
  }

  return user.email?.charAt(0).toUpperCase() || "U";
};

const getFileType = (url: string): PreviewFile["type"] => {
  const cleanUrl = url.split("?")[0].toLowerCase();

  if (
    cleanUrl.endsWith(".jpg") ||
    cleanUrl.endsWith(".jpeg") ||
    cleanUrl.endsWith(".png") ||
    cleanUrl.endsWith(".gif") ||
    cleanUrl.endsWith(".webp") ||
    cleanUrl.endsWith(".bmp") ||
    cleanUrl.endsWith(".svg")
  ) {
    return "image";
  }

  if (cleanUrl.endsWith(".pdf")) {
    return "pdf";
  }

  return "document";
};

const getWinnerFromOutcome = (dispute: Dispute): DisputeWinner | null => {
  if (dispute.outcome === "BUYER_WON") {
    return DisputeWinner.BUYER;
  }

  if (dispute.outcome === "SELLER_WON") {
    return DisputeWinner.SELLER;
  }

  return null;
};

const getWinnerRole = (dispute: Dispute): "buyer" | "seller" | null => {
  const winner = getWinnerFromOutcome(dispute);

  if (winner === DisputeWinner.BUYER) {
    return "buyer";
  }

  if (winner === DisputeWinner.SELLER) {
    return "seller";
  }

  return null;
};

const getLoserRole = (dispute: Dispute): "buyer" | "seller" | null => {
  const winnerRole = getWinnerRole(dispute);

  if (!winnerRole) {
    return null;
  }

  return winnerRole === "buyer" ? "seller" : "buyer";
};

const getWinnerLabel = (dispute: Dispute) => {
  const winner = getWinnerFromOutcome(dispute);

  if (winner === DisputeWinner.BUYER) {
    return "Buyer";
  }

  if (winner === DisputeWinner.SELLER) {
    return "Seller";
  }

  return "Not recorded";
};

const getStatusBadge = (status?: string | null) => {
  switch (status) {
    case "RESOLVED":
      return (
        <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-400">
          <CheckCircle2 className="mr-1.5 h-3 w-3" />
          Resolved
        </Badge>
      );

    case "OPEN":
      return (
        <Badge className="border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-400">
          <Clock3 className="mr-1.5 h-3 w-3" />
          Open
        </Badge>
      );

    case "PENDING":
      return (
        <Badge className="border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-50 dark:border-blue-900/40 dark:bg-blue-950/30 dark:text-blue-400">
          <Clock3 className="mr-1.5 h-3 w-3" />
          Pending
        </Badge>
      );

    case "UNDER_REVIEW":
      return (
        <Badge className="border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-50 dark:border-blue-900/40 dark:bg-blue-950/30 dark:text-blue-400">
          <ShieldAlert className="mr-1.5 h-3 w-3" />
          Under Review
        </Badge>
      );

    default:
      return <Badge variant="secondary">{status || "Unknown"}</Badge>;
  }
};

const AdminDisputeDetails = () => {
  const navigate = useNavigate();

  const { id } = useParams<{ id: string }>();

  const [previewFile, setPreviewFile] = useState<PreviewFile | null>(null);

  const [communicationOpen, setCommunicationOpen] = useState(false);

  const [verificationOpen, setVerificationOpen] = useState(false);

  const [pendingWinner, setPendingWinner] = useState<DisputeWinner | null>(
    null,
  );

  const [message, setMessage] = useState("");

  const { data, isLoading, isFetching, isError, error } = useDispute(id || "");

  const dispute = useMemo(() => data as Dispute | undefined, [data]);

  const buyer = dispute?.trade?.buyer;
  const seller = dispute?.trade?.seller;
  const openedBy = dispute?.openedBy;

  /**
   * ================================================================
   * COMPLETE TRADE CONVERSATION
   * ================================================================
   *
   * Your actual API response has:
   *
   * dispute.trade.messages
   *
   * Example:
   *
   * dispute.trade.messages = [
   *   SYSTEM,
   *   SYSTEM,
   *   USER,
   *   SYSTEM,
   *   ADMIN,
   *   ADMIN,
   *   USER,
   *   ADMIN
   * ]
   *
   * So this is the primary source.
   */
  const conversationMessages = useMemo<TradeMessage[]>(() => {
    if (!dispute) {
      return [];
    }

    if (Array.isArray(dispute.trade?.messages)) {
      return [...dispute.trade.messages].sort((a, b) => {
        const first = a.createdAt ? new Date(a.createdAt).getTime() : 0;

        const second = b.createdAt ? new Date(b.createdAt).getTime() : 0;

        return first - second;
      });
    }

    if (Array.isArray(dispute.messages)) {
      return [...dispute.messages].sort((a, b) => {
        const first = a.createdAt ? new Date(a.createdAt).getTime() : 0;

        const second = b.createdAt ? new Date(b.createdAt).getTime() : 0;

        return first - second;
      });
    }

    if (Array.isArray(dispute.chatMessages)) {
      return [...dispute.chatMessages].sort((a, b) => {
        const first = a.createdAt ? new Date(a.createdAt).getTime() : 0;

        const second = b.createdAt ? new Date(b.createdAt).getTime() : 0;

        return first - second;
      });
    }

    return [];
  }, [dispute]);

  /**
   * Get normal USER/ADMIN message text.
   */
  const getMessageText = (item: TradeMessage) => {
    return item.message || "";
  };

  /**
   * SYSTEM messages have different text for buyer
   * and seller.
   *
   * Admin is viewing the case, so show the most useful
   * available system message. If both exist, show both.
   */
  const getSystemMessageText = (item: TradeMessage) => {
    const buyerMessage = item.systemBuyerMessage?.trim() || "";

    const sellerMessage = item.systemSellerMessage?.trim() || "";

    if (buyerMessage && sellerMessage) {
      if (buyerMessage === sellerMessage) {
        return buyerMessage;
      }

      return `${buyerMessage} • ${sellerMessage}`;
    }

    return buyerMessage || sellerMessage || getMessageText(item);
  };

  /**
   * ================================================================
   * SEND ADMIN MESSAGE
   * ================================================================
   */
  const sendMessageMutation = useMutation({
    mutationFn: async () => {
      if (!dispute?.tradeId) {
        throw new Error("Trade ID is missing.");
      }

      if (dispute.status === "RESOLVED") {
        throw new Error("This dispute has already been resolved.");
      }

      const trimmedMessage = message.trim();

      if (!trimmedMessage) {
        throw new Error("Please enter a message.");
      }

      return makeRequest.post("/trade/dispute/admin", {
        tradeId: dispute.tradeId,
        message: trimmedMessage,
        imageUrl: "",
      });
    },

    onSuccess: async () => {
      toast.success("Message sent", {
        description: "Your message has been sent to the dispute participants.",
      });

      setMessage("");

      await queryClient.invalidateQueries({
        queryKey: ["disputes"],
      });

      if (dispute?.id) {
        await queryClient.invalidateQueries({
          queryKey: ["dispute", dispute.id],
        });
      }

      if (dispute?.tradeId) {
        await queryClient.invalidateQueries({
          queryKey: ["trade", dispute.tradeId],
        });
      }
    },

    onError: (mutationError) => {
      toast.error(getErrorMessage(mutationError) || "Failed to send message.");
    },
  });

  const handleSendMessage = async () => {
    if (sendMessageMutation.isPending) {
      return;
    }

    await sendMessageMutation.mutateAsync();
  };

  const resolveMutation = useMutation({
    mutationFn: async ({
      winner,
      otp,
      securityCode,
    }: {
      winner: DisputeWinner;
      otp: string;
      securityCode: string;
    }) => {
      if (!dispute?.id) {
        throw new Error("Dispute ID is missing.");
      }

      const resolution =
        winner === DisputeWinner.BUYER
          ? "Dispute resolved in favor of the buyer after evidence review."
          : "Dispute resolved in favor of the seller after evidence review.";

      return makeRequest.put(`/dispute/${dispute.id}`, {
        resolution,
        winner,
        otp,
        securityCode,
      });
    },

    onSuccess: async () => {
      toast.success("Dispute resolved successfully.");

      setVerificationOpen(false);
      setPendingWinner(null);

      await queryClient.invalidateQueries({
        queryKey: ["disputes"],
      });

      if (dispute?.id) {
        await queryClient.invalidateQueries({
          queryKey: ["dispute", dispute.id],
        });
      }

      if (dispute?.tradeId) {
        await queryClient.invalidateQueries({
          queryKey: ["trade", dispute.tradeId],
        });
      }
    },

    onError: (mutationError) => {
      toast.error(
        getErrorMessage(mutationError) || "Failed to resolve the dispute.",
      );
    },
  });

  const handleResolveClick = (winner: DisputeWinner) => {
    if (!dispute) {
      return;
    }

    if (dispute.status === "RESOLVED") {
      toast.info("This dispute has already been resolved.");
      return;
    }

    setPendingWinner(winner);
    setVerificationOpen(true);
  };

  const handleVerificationSuccess = async ({
    otp,
    securityCode,
  }: {
    otp: string;
    securityCode: string;
  }) => {
    if (!pendingWinner) {
      return;
    }

    await resolveMutation.mutateAsync({
      winner: pendingWinner,
      otp,
      securityCode,
    });
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[500px] items-center justify-center p-6">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />

          <p className="text-sm text-muted-foreground">
            Loading dispute details...
          </p>
        </div>
      </div>
    );
  }

  if (isError || !dispute) {
    return (
      <div className="space-y-4 p-4 sm:p-6 lg:p-8">
        <Button
          variant="ghost"
          className="px-0"
          onClick={() => navigate("/admin/disputes")}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to disputes
        </Button>

        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Failed to load dispute</AlertTitle>
          <AlertDescription>
            {getErrorMessage(error) ||
              "The requested dispute could not be found."}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const winnerRole = getWinnerRole(dispute);
  const loserRole = getLoserRole(dispute);
  const resolvedWinner = getWinnerFromOutcome(dispute);

  const complainantRole =
    openedBy?.id === buyer?.id
      ? "buyer"
      : openedBy?.id === seller?.id
        ? "seller"
        : null;

  const evidenceFiles = Array.isArray(dispute.images) ? dispute.images : [];

  const status = getDisputeStatus(dispute.status);

  return (
    <div className="min-h-full space-y-6 p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Button
            variant="ghost"
            className="-ml-2 w-fit hidden sm:flex"
            onClick={() => navigate("/admin/disputes")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to disputes
          </Button>

          {isFetching && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              Updating...
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline">
                <Gavel className="mr-1.5 h-3 w-3" />
                Dispute
              </Badge>

              {getStatusBadge(dispute.status)}

              {resolvedWinner && (
                <Badge
                  variant="outline"
                  className="border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-400"
                >
                  <Trophy className="mr-1.5 h-3 w-3" />
                  {getWinnerLabel(dispute)} won
                </Badge>
              )}
            </div>

            <h1 className="mt-3 break-all text-xl font-bold tracking-tight sm:text-2xl">
              Dispute #{dispute.id}
            </h1>

            <p className="mt-1 text-sm text-muted-foreground">
              Trade #{dispute.tradeId}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => setCommunicationOpen(true)}
            >
              <MessageSquare className="mr-2 h-4 w-4" />
              Communication
              {conversationMessages.length > 0 && (
                <Badge variant="secondary" className="ml-2 px-1.5">
                  {conversationMessages.length}
                </Badge>
              )}
            </Button>

            {dispute.status !== "RESOLVED" && (
              <Button onClick={() => handleResolveClick(DisputeWinner.BUYER)}>
                <Gavel className="mr-2 h-4 w-4" />
                Resolve
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card className="shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Case status
                </p>

                <div className="mt-2">{getStatusBadge(dispute.status)}</div>
              </div>

              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                <ShieldAlert className="h-5 w-5 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Evidence
                </p>

                <p className="mt-1 text-2xl font-bold">
                  {evidenceFiles.length}
                </p>

                <p className="text-xs text-muted-foreground">
                  Submitted file
                  {evidenceFiles.length === 1 ? "" : "s"}
                </p>
              </div>

              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-950/30">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Complainant
                </p>

                <p className="mt-1 truncate text-sm font-bold">
                  {getFullName(openedBy)}
                </p>

                <p className="text-xs text-muted-foreground">
                  {complainantRole
                    ? complainantRole === "buyer"
                      ? "Buyer"
                      : "Seller"
                    : "Participant"}
                </p>
              </div>

              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 dark:bg-amber-950/30">
                <UserRound className="h-5 w-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Opened
                </p>

                <p className="mt-1 text-sm font-bold">
                  {formatDate(dispute.createdAt)}
                </p>

                <p className="text-xs text-muted-foreground">
                  Case creation time
                </p>
              </div>

              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted">
                <Clock3 className="h-5 w-5 text-muted-foreground" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="min-w-0 space-y-6">
          <Card className="shadow-sm">
            <CardHeader className="border-b bg-muted/20">
              <CardTitle className="flex items-center gap-2 text-base">
                <UserRound className="h-4 w-4 text-primary" />
                Trade Participants
              </CardTitle>
            </CardHeader>

            <CardContent className="p-4 sm:p-5">
              <div className="grid gap-4 md:grid-cols-2">
                {/* BUYER */}

                <div
                  className={[
                    "rounded-xl border p-4 transition",
                    winnerRole === "buyer"
                      ? "border-emerald-300 bg-emerald-50/60 dark:border-emerald-900 dark:bg-emerald-950/20"
                      : loserRole === "buyer"
                        ? "border-red-200 bg-red-50/50 dark:border-red-950 dark:bg-red-950/20"
                        : "bg-background",
                  ].join(" ")}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-muted font-semibold">
                      {getInitials(buyer)}
                    </div>

                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-semibold">Buyer</p>

                        {winnerRole === "buyer" && (
                          <Badge className="bg-emerald-600 text-white hover:bg-emerald-600">
                            <Trophy className="mr-1 h-3 w-3" />
                            Winner
                          </Badge>
                        )}

                        {loserRole === "buyer" && (
                          <Badge
                            variant="outline"
                            className="border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-400"
                          >
                            Loser
                          </Badge>
                        )}

                        {complainantRole === "buyer" && (
                          <Badge variant="outline">Complainant</Badge>
                        )}
                      </div>

                      <p className="mt-1 truncate text-sm font-bold">
                        {getFullName(buyer)}
                      </p>

                      <p className="truncate text-xs text-muted-foreground">
                        {buyer?.email || "—"}
                      </p>
                    </div>
                  </div>

                  {buyer?.email && (
                    <div className="mt-4 border-t pt-3">
                      <a
                        href={`mailto:${buyer.email}`}
                        className="inline-flex items-center text-xs font-medium text-primary hover:underline"
                      >
                        <Mail className="mr-1.5 h-3.5 w-3.5" />
                        Contact buyer
                      </a>
                    </div>
                  )}
                </div>

                {/* SELLER */}

                <div
                  className={[
                    "rounded-xl border p-4 transition",
                    winnerRole === "seller"
                      ? "border-emerald-300 bg-emerald-50/60 dark:border-emerald-900 dark:bg-emerald-950/20"
                      : loserRole === "seller"
                        ? "border-red-200 bg-red-50/50 dark:border-red-950 dark:bg-red-950/20"
                        : "bg-background",
                  ].join(" ")}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-muted font-semibold">
                      {getInitials(seller)}
                    </div>

                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-semibold">Seller</p>

                        {winnerRole === "seller" && (
                          <Badge className="bg-emerald-600 text-white hover:bg-emerald-600">
                            <Trophy className="mr-1 h-3 w-3" />
                            Winner
                          </Badge>
                        )}

                        {loserRole === "seller" && (
                          <Badge
                            variant="outline"
                            className="border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-400"
                          >
                            Loser
                          </Badge>
                        )}

                        {complainantRole === "seller" && (
                          <Badge variant="outline">Complainant</Badge>
                        )}
                      </div>

                      <p className="mt-1 truncate text-sm font-bold">
                        {getFullName(seller)}
                      </p>

                      <p className="truncate text-xs text-muted-foreground">
                        {seller?.email || "—"}
                      </p>
                    </div>
                  </div>

                  {seller?.email && (
                    <div className="mt-4 border-t pt-3">
                      <a
                        href={`mailto:${seller.email}`}
                        className="inline-flex items-center text-xs font-medium text-primary hover:underline"
                      >
                        <Mail className="mr-1.5 h-3.5 w-3.5" />
                        Contact seller
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ======================================================== */}
          {/* DISPUTE REASON                                            */}
          {/* ======================================================== */}

          <Card className="shadow-sm">
            <CardHeader className="border-b bg-muted/20">
              <CardTitle className="flex items-center gap-2 text-base">
                <MessageSquare className="h-4 w-4 text-primary" />
                Dispute Reason
              </CardTitle>
            </CardHeader>

            <CardContent className="p-4 sm:p-5">
              <div className="rounded-xl border bg-muted/20 p-4">
                <p className="whitespace-pre-wrap text-sm leading-7">
                  {dispute.reason || "No reason or message was provided."}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* ======================================================== */}
          {/* EVIDENCE                                                  */}
          {/* ======================================================== */}

          <Card className="shadow-sm">
            <CardHeader className="border-b bg-muted/20">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <FileText className="h-4 w-4 text-primary" />
                    Submitted Evidence
                  </CardTitle>

                  <p className="mt-1 text-xs text-muted-foreground">
                    Review all submitted files before making a final decision.
                  </p>
                </div>

                <Badge variant="outline">
                  {evidenceFiles.length} file
                  {evidenceFiles.length === 1 ? "" : "s"}
                </Badge>
              </div>
            </CardHeader>

            <CardContent className="p-4 sm:p-5">
              {evidenceFiles.length === 0 ? (
                <div className="flex min-h-[180px] flex-col items-center justify-center rounded-xl border border-dashed text-center">
                  <FileText className="h-10 w-10 text-muted-foreground" />

                  <p className="mt-3 text-sm font-medium">
                    No evidence submitted
                  </p>

                  <p className="mt-1 text-xs text-muted-foreground">
                    This dispute does not contain any uploaded evidence.
                  </p>
                </div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {evidenceFiles.map((url, index) => {
                    const fileType = getFileType(url);

                    return (
                      <button
                        key={`${url}-${index}`}
                        type="button"
                        className="group overflow-hidden rounded-xl border bg-background text-left transition hover:border-primary/50 hover:shadow-md"
                        onClick={() =>
                          setPreviewFile({
                            url,
                            type: fileType,
                          })
                        }
                      >
                        {fileType === "image" && (
                          <div className="aspect-video overflow-hidden bg-muted">
                            <img
                              src={url}
                              alt={`Evidence ${index + 1}`}
                              className="h-full w-full object-cover transition duration-200 group-hover:scale-105"
                            />
                          </div>
                        )}

                        {fileType === "pdf" && (
                          <div className="flex aspect-video items-center justify-center bg-muted">
                            <FileText className="h-12 w-12 text-red-500" />
                          </div>
                        )}

                        {fileType === "document" && (
                          <div className="flex aspect-video items-center justify-center bg-muted">
                            <FileText className="h-12 w-12 text-muted-foreground" />
                          </div>
                        )}

                        <div className="flex items-center justify-between gap-3 p-3">
                          <div className="flex min-w-0 items-center gap-2">
                            {fileType === "image" ? (
                              <ImageIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                            ) : (
                              <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                            )}

                            <span className="truncate text-xs font-medium">
                              Evidence {index + 1}
                            </span>
                          </div>

                          <ZoomIn className="h-4 w-4 shrink-0 text-muted-foreground" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* ======================================================== */}
          {/* TIMELINE                                                  */}
          {/* ======================================================== */}

          <Card className="shadow-sm">
            <CardHeader className="border-b bg-muted/20">
              <CardTitle className="flex items-center gap-2 text-base">
                <Clock3 className="h-4 w-4 text-primary" />
                Case Timeline
              </CardTitle>
            </CardHeader>

            <CardContent className="p-5">
              <div className="relative space-y-6">
                <div className="relative flex gap-4">
                  <div className="relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-950/40">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                  </div>

                  <div className="min-w-0">
                    <p className="text-sm font-semibold">Dispute opened</p>

                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {formatDate(dispute.createdAt)}
                    </p>

                    <p className="mt-2 text-sm leading-6 text-muted-foreground">
                      {getFullName(openedBy)} opened the dispute.
                    </p>
                  </div>
                </div>

                {dispute.status === "RESOLVED" && (
                  <div className="relative flex gap-4">
                    <div className="relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-950/40">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    </div>

                    <div className="min-w-0">
                      <p className="text-sm font-semibold">Dispute resolved</p>

                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {formatDate(dispute.resolvedAt)}
                      </p>

                      <p className="mt-2 text-sm leading-6 text-muted-foreground">
                        {dispute.outcome === "BUYER_WON"
                          ? "The buyer was awarded the dispute."
                          : dispute.outcome === "SELLER_WON"
                            ? "The seller was awarded the dispute."
                            : "The dispute was marked as resolved."}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ============================================================ */}
        {/* RIGHT SIDEBAR                                                 */}
        {/* ============================================================ */}

        <div className="space-y-6">
          <Card className="shadow-sm">
            <CardHeader className="border-b bg-muted/20">
              <CardTitle className="text-base">Case Information</CardTitle>
            </CardHeader>

            <CardContent className="space-y-4 p-5">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Dispute ID
                </p>

                <p className="mt-1 break-all font-mono text-xs">{dispute.id}</p>
              </div>

              <Separator />

              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Trade ID
                </p>

                <p className="mt-1 break-all font-mono text-xs">
                  {dispute.tradeId}
                </p>
              </div>

              <Separator />

              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Opened by
                </p>

                <p className="mt-1 text-sm font-semibold">
                  {getFullName(openedBy)}
                </p>

                <p className="text-xs text-muted-foreground">
                  {openedBy?.email || "—"}
                </p>

                {complainantRole && (
                  <Badge variant="outline" className="mt-2">
                    {complainantRole === "buyer" ? "Buyer" : "Seller"}
                  </Badge>
                )}
              </div>

              <Separator />

              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Created
                </p>

                <p className="mt-1 text-sm font-medium">
                  {formatDate(dispute.createdAt)}
                </p>
              </div>

              {dispute.resolvedAt && (
                <>
                  <Separator />

                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Resolved
                    </p>

                    <p className="mt-1 text-sm font-medium">
                      {formatDate(dispute.resolvedAt)}
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* FINAL DECISION */}

          {dispute.status === "RESOLVED" && (
            <Card className="overflow-hidden border-emerald-200 shadow-sm dark:border-emerald-900/50">
              <CardHeader className="border-b border-emerald-200 bg-emerald-50/70 dark:border-emerald-900/50 dark:bg-emerald-950/20">
                <CardTitle className="flex items-center gap-2 text-base text-emerald-800 dark:text-emerald-400">
                  <Trophy className="h-5 w-5" />
                  Final Decision
                </CardTitle>
              </CardHeader>

              <CardContent className="space-y-5 p-5">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Winner
                  </p>

                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-950/50">
                      <Trophy className="h-4 w-4 text-emerald-600" />
                    </div>

                    <p className="text-lg font-bold">
                      {getWinnerLabel(dispute)}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Loser
                  </p>

                  <p className="mt-1 text-sm font-semibold">
                    {resolvedWinner === DisputeWinner.BUYER
                      ? "Seller"
                      : resolvedWinner === DisputeWinner.SELLER
                        ? "Buyer"
                        : "Not recorded"}
                  </p>
                </div>

                {dispute.resolution && (
                  <>
                    <Separator />

                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Resolution
                      </p>

                      <p className="mt-2 text-sm leading-6">
                        {dispute.resolution}
                      </p>
                    </div>
                  </>
                )}

                {dispute.resolvedBy && (
                  <>
                    <Separator />

                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Resolved by
                      </p>

                      <p className="mt-1 text-sm font-semibold">
                        {getFullName(dispute.resolvedBy)}
                      </p>

                      <p className="text-xs text-muted-foreground">
                        {dispute.resolvedBy.email || "—"}
                      </p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* RESOLUTION ACTION */}

          {dispute.status !== "RESOLVED" && (
            <Card className="shadow-sm">
              <CardHeader className="border-b bg-muted/20">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Gavel className="h-4 w-4 text-primary" />
                  Final Resolution
                </CardTitle>
              </CardHeader>

              <CardContent className="space-y-4 p-5">
                <div className="rounded-xl border bg-muted/20 p-4">
                  <p className="text-sm font-semibold">
                    Administrative decision
                  </p>

                  <p className="mt-1 text-xs leading-5 text-muted-foreground">
                    Carefully review the complaint and evidence before making a
                    final decision. This action will permanently resolve the
                    case according to your backend resolution logic.
                  </p>
                </div>

                <div className="grid gap-3">
                  <Button
                    variant="outline"
                    className="h-11 w-full"
                    disabled={resolveMutation.isPending}
                    onClick={() => handleResolveClick(DisputeWinner.BUYER)}
                  >
                    <Trophy className="mr-2 h-4 w-4" />
                    Award to Buyer
                  </Button>

                  <Button
                    className="h-11 w-full"
                    disabled={resolveMutation.isPending}
                    onClick={() => handleResolveClick(DisputeWinner.SELLER)}
                  >
                    <Trophy className="mr-2 h-4 w-4" />
                    Award to Seller
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* ============================================================ */}
      {/* UNIFIED DISPUTE COMMUNICATION MODAL                          */}
      {/* ============================================================ */}

      <TradeCommunicationDialog
        open={communicationOpen}
        onOpenChange={setCommunicationOpen}
        tradeId={dispute.tradeId}
        messages={conversationMessages}
        trade={dispute.trade as unknown as Trade}
        dispute={dispute}
        status={status}
        message={message}
        onMessageChange={setMessage}
        onSendMessage={handleSendMessage}
        isSending={sendMessageMutation.isPending}
      />

      {/* ============================================================ */}
      {/* FILE PREVIEW                                                  */}
      {/* ============================================================ */}

      <Dialog
        open={!!previewFile}
        onOpenChange={(open) => {
          if (!open) {
            setPreviewFile(null);
          }
        }}
      >
        <DialogContent className="h-[90vh] max-w-6xl overflow-hidden p-0">
          <div className="flex h-full min-h-0 flex-col">
            <div className="flex shrink-0 items-center justify-between border-b px-4 py-3">
              <div>
                <DialogTitle>Evidence Preview</DialogTitle>

                {previewFile && (
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Review submitted evidence or message attachment
                  </p>
                )}
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={() => setPreviewFile(null)}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="min-h-0 flex-1 overflow-auto bg-muted/20 p-4">
              {previewFile?.type === "image" && (
                <div className="flex min-h-full items-center justify-center">
                  <img
                    src={previewFile.url}
                    alt="Preview"
                    className="max-h-full max-w-full rounded-lg object-contain shadow-sm"
                  />
                </div>
              )}

              {previewFile?.type === "pdf" && (
                <iframe
                  src={previewFile.url}
                  title="PDF evidence preview"
                  className="h-full min-h-[70vh] w-full rounded-lg border bg-background"
                />
              )}

              {previewFile?.type === "document" && (
                <div className="flex min-h-[60vh] flex-col items-center justify-center rounded-xl border bg-background p-8 text-center">
                  <FileText className="h-14 w-14 text-muted-foreground" />

                  <h3 className="mt-4 text-lg font-semibold">
                    Document Preview
                  </h3>

                  <p className="mt-1 max-w-md text-sm leading-6 text-muted-foreground">
                    This file type cannot be displayed directly inside the
                    application.
                  </p>

                  <Button
                    className="mt-5"
                    onClick={() =>
                      window.open(
                        previewFile.url,
                        "_blank",
                        "noopener,noreferrer",
                      )
                    }
                  >
                    Open Document
                  </Button>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============================================================ */}
      {/* ADMIN VERIFICATION                                            */}
      {/* ============================================================ */}

      <AdminVerificationDialog
        open={verificationOpen}
        onOpenChange={(open) => {
          if (!resolveMutation.isPending) {
            setVerificationOpen(open);

            if (!open) {
              setPendingWinner(null);
            }
          }
        }}
        onSuccess={handleVerificationSuccess}
        systemType="dispute"
      />
    </div>
  );
};

export default AdminDisputeDetails;
